import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  buyerSubmissionsApi,
  type BrokerIntroEmailStateDto,
} from "@/api/buyers";
import { useToast } from "@/design-system/components/ui";
import { buyerSubmissionKeys } from "@/hooks/buyers/buyerKeys";
import { SendPreviewModal } from "../sendPreview/SendPreviewModal";
import { useSenderIdentity } from "../sendPreview/useSenderIdentity";

/**
 * The Phoenix send — one confirmation naming the listing broker, with the
 * message itself as the last thing on screen before it goes.
 *
 * <p>🔴 <b>The recipient is stated, not implied.</b> The submission carries TWO
 * intro composers whose audiences are opposites — the buyer intro (its own tab)
 * goes to the buyer, this one goes to an outside broker. A confirmation that
 * said only "Send?" would be the one place a specialist could act on the wrong
 * audience without a word of warning.</p>
 *
 * <p>🔴 <b>It invalidates the SUBMISSION prefix and nothing else.</b> The buyer
 * intro's own modal additionally invalidates <c>buyerKeys.all</c> because
 * sending it stamps the buyer's contact clocks; this send deliberately stamps
 * none of them — a broker email must never make the buyer look contacted — so
 * there is nothing on the buyer's own reads to refresh. Adding that prefix here
 * would be harmless today and would read as evidence the stamp exists.</p>
 */
export function BrokerIntroSendModal({
  submissionId,
  email,
  onSent,
  onClose,
}: {
  submissionId: string;
  email: BrokerIntroEmailStateDto;
  /** Called once the send succeeds — the tab's collapse-and-advance (J6). */
  onSent?: () => void;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const identity = useSenderIdentity();

  const send = useMutation({
    mutationFn: () => buyerSubmissionsApi.sendBrokerIntro(submissionId),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      toast.success(`Sent to ${r.toEmail}.`);
      onSent?.();
      onClose();
    },
    // Every refusal happens before the transport call, so an error here always
    // means nothing left the building — the message can be shown as-is.
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <SendPreviewModal
      title="Send the broker intro"
      onClose={onClose}
      toName={email.agentName}
      toEmail={email.agentEmail}
      audienceNote="under your name. It goes to the listing broker, not to the buyer."
      identity={identity}
      subject={email.subject}
      body={email.bodyDraft}
      signatureBlock={email.signatureBlock}
      roleStatement={email.roleStatement}
      confirmLabel="Send from Phoenix"
      confirmTestId="broker-intro-send-confirm"
      confirmDisabled={!email.canSend}
      confirmBlockedReason={email.canSend ? undefined : email.blockedReason}
      onConfirm={() => send.mutate()}
      pending={send.isPending}
      footnote={
        <>
          Your sign-off and Phoenix&apos;s role statement are added
          automatically. Sending records this broker intro as sent; it does not
          change the submission&apos;s status, it does not send the package, and
          it does not mark the buyer as contacted.
        </>
      }
    />
  );
}
